/**
 * Hook to get current authentication context
 * Provides easy access to current user, tenant, and role information
 */

import { useAuthStore } from '../../features/authentication/store/authStore';

export const useAuthContext = () => {
  const { user, currentTenant, userSession, isAuthenticated, getCurrentRoles, hasRole } =
    useAuthStore();

  // Get current user ID for assignment/audit purposes
  const getCurrentUserId = () => {
    return user?.id || 'unknown-user';
  };

  // Get current user email for assignment/audit purposes
  const getCurrentUserEmail = () => {
    return user?.email || 'unknown-user';
  };

  // Get current tenant ID
  const getCurrentTenantId = () => {
    return currentTenant?.id || userSession?.currentTenantId;
  };

  // Get system admin tenant ID (for system-level operations)
  const getSystemAdminTenantId = () => {
    // Use the current tenant ID for system admin operations
    // System admins should be created in the same tenant as the inviting admin
    return getCurrentTenantId() || '66d4fbeb-5c8a-4c8b-acab-f9ebfbda7539';
  };

  // Get assigned by information for role assignments
  const getAssignedByInfo = () => {
    return {
      userId: getCurrentUserId(),
      email: getCurrentUserEmail(),
      name: user?.name || user?.email || 'Unknown User',
    };
  };

  // Check if user has system admin privileges
  const isSystemAdmin = () => {
    return hasRole('SYSTEM_ADMIN');
  };

  // Check if user has university admin privileges for current tenant
  const isUniversityAdmin = () => {
    return hasRole('UNIVERSITY_ADMIN');
  };

  // Check if user has institute admin privileges
  const isInstituteAdmin = () => {
    return hasRole('FACULTY_ADMIN');
  };

  // Get all current roles
  const getCurrentUserRoles = () => {
    return getCurrentRoles();
  };

  return {
    // Basic auth info
    user,
    currentTenant,
    userSession,
    isAuthenticated,

    // Helper functions
    getCurrentUserId,
    getCurrentUserEmail,
    getCurrentTenantId,
    getSystemAdminTenantId,
    getAssignedByInfo,

    // Role checks
    isSystemAdmin,
    isUniversityAdmin,
    isInstituteAdmin,
    hasRole,
    getCurrentUserRoles,
  };
};
